import { ArrowBack, Download, Memory, MonitorHeart, NetworkCheck, Refresh } from '@mui/icons-material';
import { Box, Button, CircularProgress, FormControl, IconButton, InputLabel, LinearProgress, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import util from '../util';

const MIN_POLL_MS = 250;
const MAX_TIMESCALE_MS = 60 * 60 * 1000;
const TIMESCALE_OPTIONS = [
    { label: '10 sec', value: 10_000 },
    { label: '30 sec', value: 30_000 },
    { label: '1 min', value: 60_000 },
    { label: '5 min', value: 300_000 },
    { label: '15 min', value: 900_000 },
    { label: '30 min', value: 1_800_000 },
    { label: '1 hr', value: 3_600_000 },
];

function formatBytes(bytes) {
    if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return 'N/A';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = Number(bytes);
    let idx = 0;
    while (value >= 1024 && idx < units.length - 1) {
        value /= 1024;
        idx++;
    }
    return `${value.toFixed(2)} ${units[idx]}`;
}

function formatBytesPerSecond(bytes) {
    if (!bytes || bytes <= 0) return '0 B/s';
    const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    let value = bytes;
    let idx = 0;
    while (value >= 1024 && idx < units.length - 1) {
        value /= 1024;
        idx++;
    }
    return `${value.toFixed(2)} ${units[idx]}`;
}

function clampPercent(value) {
    if (Number.isNaN(Number(value))) return 0;
    return Math.max(0, Math.min(100, Number(value)));
}

function LegendItem({ color, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, mr: 0.8 }} />
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{label}</Typography>
        </Box>
    );
}

function Sparkline({
    points = [],
    color = '#9f3aeb',
    secondColor = null,
    maxY = 100,
    height = 200,
    primaryLabel = 'Primary',
    secondaryLabel = 'Secondary',
    formatPrimary = (v) => v.toFixed(2),
    formatSecondary = (v) => v.toFixed(2),
    formatAxis = (v) => `${Math.round(v)}`,
}) {
    const width = 700;
    const axisLeft = 60;
    const axisRight = 8;
    const plotWidth = width - axisLeft - axisRight;
    const [hoverIndex, setHoverIndex] = useState(null);

    const firstLine = useMemo(() => {
        if (!points.length) return '';
        return points
            .map((point, index) => {
                const x = axisLeft + (index / Math.max(points.length - 1, 1)) * plotWidth;
                const y = height - (Math.max(0, Math.min(maxY, point.y1 || 0)) / maxY) * height;
                return `${x},${y}`;
            })
            .join(' ');
    }, [points, maxY, height, axisLeft, plotWidth]);

    const secondLine = useMemo(() => {
        if (!points.length || !secondColor) return '';
        return points
            .map((point, index) => {
                const x = axisLeft + (index / Math.max(points.length - 1, 1)) * plotWidth;
                const y = height - (Math.max(0, Math.min(maxY, point.y2 || 0)) / maxY) * height;
                return `${x},${y}`;
            })
            .join(' ');
    }, [points, maxY, secondColor, height, axisLeft, plotWidth]);

    const hoverPoint = hoverIndex !== null ? points[hoverIndex] : null;
    const hoverX = hoverIndex !== null
        ? axisLeft + (hoverIndex / Math.max(points.length - 1, 1)) * plotWidth
        : null;
    const hoverY1 = hoverPoint
        ? height - (Math.max(0, Math.min(maxY, hoverPoint.y1 || 0)) / maxY) * height
        : null;
    const hoverY2 = hoverPoint && secondColor
        ? height - (Math.max(0, Math.min(maxY, hoverPoint.y2 || 0)) / maxY) * height
        : null;

    const cursorTimeLabel = hoverPoint?.t
        ? new Date(hoverPoint.t).toLocaleTimeString()
        : null;

    const handleMouseMove = (ev) => {
        if (!points.length) return;
        const rect = ev.currentTarget.getBoundingClientRect();
        const rawX = ev.clientX - rect.left;
        const normalized = rect.width > 0 ? rawX / rect.width : 0;
        const svgX = Math.max(0, Math.min(1, normalized)) * width;
        const clampedPlotX = Math.max(axisLeft, Math.min(width - axisRight, svgX));
        const plotRatio = (clampedPlotX - axisLeft) / Math.max(plotWidth, 1);
        const idx = Math.max(0, Math.min(points.length - 1, Math.round(plotRatio * Math.max(points.length - 1, 1))));
        setHoverIndex(idx);
    };

    return (
        <Box sx={{ width: '100%', backgroundColor: '#111', borderRadius: 1, p: 1, position: 'relative' }}>
            <svg
                width="100%"
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHoverIndex(null)}
            >
                <line x1={axisLeft} y1={height} x2={width - axisRight} y2={height} stroke="#2d2d2d" strokeWidth="1" />
                <line x1={axisLeft} y1={height * 0.5} x2={width - axisRight} y2={height * 0.5} stroke="#2d2d2d" strokeWidth="1" />
                <line x1={axisLeft} y1="0" x2={width - axisRight} y2="0" stroke="#2d2d2d" strokeWidth="1" />
                <text x="4" y="10" fill="#8c8c8c" fontSize="10">{formatAxis(maxY)}</text>
                <text x="4" y={height * 0.5 + 4} fill="#8c8c8c" fontSize="10">{formatAxis(maxY * 0.5)}</text>
                <text x="4" y={height - 2} fill="#8c8c8c" fontSize="10">{formatAxis(0)}</text>
                {secondLine && <polyline fill="none" stroke={secondColor} strokeWidth="2" points={secondLine} />}
                {firstLine && <polyline fill="none" stroke={color} strokeWidth="2" points={firstLine} />}
                {hoverX !== null && (
                    <>
                        <line x1={hoverX} y1="0" x2={hoverX} y2={height} stroke="#ffffff66" strokeWidth="1" strokeDasharray="3 3" />
                        {hoverY2 !== null && <circle cx={hoverX} cy={hoverY2} r="3" fill={secondColor || '#fff'} />}
                        {hoverY1 !== null && <circle cx={hoverX} cy={hoverY1} r="3" fill={color} />}
                    </>
                )}
            </svg>

            {hoverPoint && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        backgroundColor: '#000000cc',
                        border: '1px solid #444',
                        borderRadius: 1,
                        px: 1,
                        py: 0.75,
                        pointerEvents: 'none',
                    }}
                >
                    <Typography sx={{ fontSize: 11, opacity: 0.8 }}>{cursorTimeLabel || 'Cursor'}</Typography>
                    <Typography sx={{ fontSize: 12 }}>
                        {primaryLabel}: {formatPrimary(Number(hoverPoint.y1 || 0))}
                    </Typography>
                    {secondColor && (
                        <Typography sx={{ fontSize: 12 }}>
                            {secondaryLabel}: {formatSecondary(Number(hoverPoint.y2 || 0))}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

const ServerStatusPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [timescaleMs, setTimescaleMs] = useState(() => {
        const saved = Number(localStorage.getItem('serverStatusTimescaleMs') || 60_000);
        const validValues = TIMESCALE_OPTIONS.map((x) => x.value);
        return validValues.includes(saved) ? saved : 60_000;
    });
    const [pollIntervalMs, setPollIntervalMs] = useState(() => {
        const saved = Number(localStorage.getItem('serverStatusPollMs') || 1000);
        return Number.isFinite(saved) ? Math.max(MIN_POLL_MS, saved) : 1000;
    });
    const [pollInput, setPollInput] = useState(String(pollIntervalMs));
    const maxHistoryPoints = useMemo(() => {
        return Math.max(240, Math.ceil(MAX_TIMESCALE_MS / Math.max(pollIntervalMs, MIN_POLL_MS)) + 5);
    }, [pollIntervalMs]);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await util.getRequest('/api/serverstatus');
            if (!res || res.code === 401 || res.code === 500) {
                throw new Error('Failed to fetch server status');
            }

            const cpu = clampPercent(res?.cpu?.usagePercent || 0);
            const memory = clampPercent(res?.memory?.usagePercent || 0);
            const recv = Number(res?.network?.receivedBytesPerSecond || 0);
            const sent = Number(res?.network?.sentBytesPerSecond || 0);

            setStatus(res);
            setError('');
            setHistory((prev) => {
                const next = [...prev, { t: Date.now(), y1: cpu, y2: memory, recv, sent }];
                if (next.length > maxHistoryPoints) {
                    return next.slice(next.length - maxHistoryPoints);
                }
                return next;
            });
        } catch (e) {
            setError(e.message || 'Failed to fetch server status');
        } finally {
            setLoading(false);
        }
    }, [maxHistoryPoints]);

    const applyPollInterval = () => {
        const parsed = Number(pollInput);
        if (!Number.isFinite(parsed)) {
            setPollInput(String(pollIntervalMs));
            return;
        }

        const clamped = Math.max(MIN_POLL_MS, Math.floor(parsed));
        setPollIntervalMs(clamped);
        setPollInput(String(clamped));
        localStorage.setItem('serverStatusPollMs', String(clamped));
    };

    useEffect(() => {
        fetchStatus();
        const timer = setInterval(fetchStatus, pollIntervalMs);
        return () => clearInterval(timer);
    }, [fetchStatus, pollIntervalMs]);

    const windowedHistory = useMemo(() => {
        const cutoff = Date.now() - timescaleMs;
        return history.filter((p) => p.t >= cutoff);
    }, [history, timescaleMs]);

    const cpuPoints = windowedHistory.map((p) => ({ y1: p.y1, t: p.t }));
    const memoryPoints = windowedHistory.map((p) => ({ y1: p.y2, t: p.t }));

    const maxNet = Math.max(1, ...windowedHistory.map((p) => Math.max(p.recv, p.sent)));
    const networkPoints = windowedHistory.map((p) => ({ y1: p.recv, y2: p.sent, t: p.t }));

    const selectedTimescaleLabel = TIMESCALE_OPTIONS.find((x) => x.value === timescaleMs)?.label || `${timescaleMs} ms`;

    return (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#151515', borderRadius: 1, minHeight: 'calc(100vh - 110px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => navigate('/contentManager')}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ ml: 1 }}>Server Status</Typography>
                <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel id="timescale-label">Timescale</InputLabel>
                        <Select
                            labelId="timescale-label"
                            value={timescaleMs}
                            label="Timescale"
                            onChange={(e) => {
                                const next = Number(e.target.value);
                                setTimescaleMs(next);
                                localStorage.setItem('serverStatusTimescaleMs', String(next));
                            }}
                        >
                            {TIMESCALE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        type="number"
                        label="Poll (ms)"
                        value={pollInput}
                        inputProps={{ min: MIN_POLL_MS, step: 50 }}
                        onChange={(e) => setPollInput(e.target.value)}
                        onBlur={applyPollInterval}
                        sx={{ width: 130 }}
                    />
                    <Button variant="outlined" size="small" onClick={applyPollInterval}>Apply</Button>
                    <Typography sx={{ fontSize: 12, opacity: 0.75 }}>Min {MIN_POLL_MS} ms</Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                    <Button startIcon={<Refresh />} onClick={fetchStatus}>Refresh now</Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ mt: '25vh', textAlign: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box sx={{ backgroundColor: '#1d1d1d', p: 2, borderRadius: 1 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>System Overview</Typography>
                        <Typography sx={{ opacity: 0.9 }}>Host OS: {status?.hostOs || 'Unavailable (non-admin or not provided)'}</Typography>
                        <Typography sx={{ opacity: 0.9 }}>CPU Model: {status?.cpu?.model || 'Unavailable (non-admin or not provided)'}</Typography>
                        <Typography sx={{ opacity: 0.9 }}>CPU Usage: {clampPercent(status?.cpu?.usagePercent || 0).toFixed(2)}%</Typography>
                        <Typography sx={{ opacity: 0.9 }}>
                            RAM Used: {status?.memory?.usedBytes !== undefined ? formatBytes(status.memory.usedBytes) : 'Unavailable'}
                            {' / '}
                            {status?.memory?.totalBytes !== undefined ? formatBytes(status.memory.totalBytes) : 'Unavailable'}
                            {' ('}{clampPercent(status?.memory?.usagePercent || 0).toFixed(2)}%{')'}
                        </Typography>
                    </Box>

                    <Box sx={{ backgroundColor: '#1d1d1d', p: 2, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <MonitorHeart sx={{ mr: 1 }} />
                            <Typography variant="h6">CPU Usage ({clampPercent(status?.cpu?.usagePercent || 0).toFixed(2)}%)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LegendItem color="#9f3aeb" label="CPU %" />
                        </Box>
                        <LinearProgress variant="determinate" value={clampPercent(status?.cpu?.usagePercent || 0)} sx={{ mb: 1.5 }} />
                        <Sparkline
                            points={cpuPoints}
                            color="#9f3aeb"
                            maxY={100}
                            primaryLabel="CPU"
                            formatPrimary={(v) => `${v.toFixed(2)}%`}
                            formatAxis={(v) => `${Math.round(v)}%`}
                        />
                    </Box>

                    <Box sx={{ backgroundColor: '#1d1d1d', p: 2, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Memory sx={{ mr: 1 }} />
                            <Typography variant="h6">Memory Usage ({clampPercent(status?.memory?.usagePercent || 0).toFixed(2)}%)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LegendItem color="#00bcd4" label="Memory %" />
                        </Box>
                        <Typography sx={{ opacity: 0.85, mb: 1 }}>
                            Used: {status?.memory?.usedBytes !== undefined ? formatBytes(status.memory.usedBytes) : 'Unavailable'} • Total: {status?.memory?.totalBytes !== undefined ? formatBytes(status.memory.totalBytes) : 'Unavailable'}
                        </Typography>
                        <LinearProgress variant="determinate" value={clampPercent(status?.memory?.usagePercent || 0)} sx={{ mb: 1.5 }} />
                        <Sparkline
                            points={memoryPoints}
                            color="#00bcd4"
                            maxY={100}
                            primaryLabel="Memory"
                            formatPrimary={(v) => `${v.toFixed(2)}%`}
                            formatAxis={(v) => `${Math.round(v)}%`}
                        />
                    </Box>

                    <Box sx={{ backgroundColor: '#1d1d1d', p: 2, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <NetworkCheck sx={{ mr: 1 }} />
                            <Typography variant="h6">Network Throughput</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LegendItem color="#4caf50" label="Receive bytes/s" />
                            <LegendItem color="#ff9800" label="Send bytes/s" />
                        </Box>
                        <Typography sx={{ opacity: 0.85, mb: 1 }}>
                            Receive: {formatBytesPerSecond(status?.network?.receivedBytesPerSecond || 0)} • Send: {formatBytesPerSecond(status?.network?.sentBytesPerSecond || 0)}
                        </Typography>
                        <Typography sx={{ opacity: 0.85, mb: 1 }}>
                            Total Received: {formatBytes(status?.network?.totalReceivedBytes || 0)} • Total Sent: {formatBytes(status?.network?.totalSentBytes || 0)}
                        </Typography>
                        <Sparkline
                            points={networkPoints}
                            color="#4caf50"
                            secondColor="#ff9800"
                            maxY={maxNet}
                            primaryLabel="Receive"
                            secondaryLabel="Send"
                            formatPrimary={(v) => formatBytesPerSecond(v)}
                            formatSecondary={(v) => formatBytesPerSecond(v)}
                            formatAxis={(v) => formatBytesPerSecond(v)}
                        />
                        <Typography sx={{ mt: 1, opacity: 0.75, fontSize: 12 }}>
                            Polling every {pollIntervalMs} ms • Showing last {selectedTimescaleLabel}
                        </Typography>
                    </Box>

                    {status?.storage && (
                        <Box sx={{ backgroundColor: '#1d1d1d', p: 2, borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Download sx={{ mr: 1 }} />
                                <Typography variant="h6">Storage Snapshot</Typography>
                            </Box>
                            <Typography sx={{ opacity: 0.85 }}>
                                Data: {(status.storage.totalStoredDataMb || 0).toFixed ? status.storage.totalStoredDataMb.toFixed(2) : status.storage.totalStoredDataMb} MB • Files: {status.storage.fileCount} • Folders: {status.storage.folderCount} • Thumbnails: {status.storage.thumbnailCount}
                            </Typography>
                            <Typography sx={{ opacity: 0.8 }}>
                                Standard Thumbnails: {status.storage.standardThumbnailCount} • Large Thumbnails: {status.storage.largeThumbnailCount}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ backgroundColor: '#1d1d1d', p: 2, borderRadius: 1 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Raw Payload</Typography>
                        <Box
                            component="pre"
                            sx={{
                                m: 0,
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: '#111',
                                overflowX: 'auto',
                                fontSize: 12,
                                lineHeight: 1.4,
                            }}
                        >
                            {JSON.stringify(status, null, 2)}
                        </Box>
                        <Typography sx={{ mt: 1, fontSize: 12, opacity: 0.7 }}>
                            Some fields are admin-only from the backend and may appear unavailable for non-admin users.
                            </Typography>
                        </Box>
                    
                </Box>
            )}
        </Box>
    );
};

export default ServerStatusPage;
